/**
 * @name formatToMarketSpecificCurrency
 * @deprecated This utility is deprecated and will be removed in the next major version. Please, use [PLS.UI](https://pages.git.i.mercedes-benz.com/dh-io-pls/dcps-product-pricing/) instead.
 * @description Provides special formatting for selected Mercedes-Benz markets, which don't represent the international default format. Use `formatToCurrency()` for international standard currency formatting.
 *
 * @example
 * import { formatToMarketSpecificCurrency } from '@workbench/core';
 *
 * formatToMarketSpecificCurrency(120000, 'it-CH'); // 'CHF 1’234’568.-'
 * formatToMarketSpecificCurrency(120000, 'unsupported-LOCALE'); // undefined
 */
export const formatToMarketSpecificCurrency = (value: number, locale: SupportedLocale, config: { [locale in SupportedLocale]: CurrencyConfig } = CUSTOM_CURRENCY_FORMATS): string => {
  if (!config[locale]) {
    return;
  }

  const { prefix = '', suffix = '', decimals = 0, preferLocale = '' } = config[locale];

  const price = new Intl.NumberFormat(preferLocale || locale, { style: 'currency', currency: 'EUR', minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    .format(value)
    .replace(/\s*€\s*/, '')
    .replace(/\s*EUR\s*/, '');

  return `${prefix}${price}${suffix}`.trim();
};

export type SupportedLocale = 'cs-CZ' | 'da-DK' | 'de-AT' | 'de-CH' | 'de-DE' | 'de-HU' | 'de-LU' | 'el-CY' | 'el-GR' | 'en-AE' | 'en-AU' | 'en-CH' | 'en-CN' | 'en-DE' | 'en-EG' | 'en-GB' | 'en-HK' | 'en-HU' | 'en-IE' | 'en-IN' | 'en-KR' | 'en-MX' | 'en-MY' | 'en-NZ' | 'en-SE' | 'en-SG' | 'en-TH' | 'en-TW' | 'en-VN' | 'en-ZA' | 'es-AR' | 'es-ES' | 'es-MX' | 'fi-FI' | 'fr-BE' | 'fr-CH' | 'fr-FR' | 'fr-LU' | 'hi-IN' | 'hu-HU' | 'it-CH' | 'it-IT' | 'ja-JP' | 'ko-KR' | 'nl-BE' | 'nl-NL' | 'no-NO' | 'pl-PL' | 'pt-BR' | 'pt-PT' | 'ro-RO' | 'ru-RU' | 'sk-SK' | 'sl-SI' | 'sr-RS' | 'sv-SE' | 'th-TH' | 'tr-TR' | 'tr-ZY' | 'vi-VN' | 'zh-CN' | 'zh-HK' | 'zh-TW' | 'hr-BA' | 'uk-UA' | 'ar-EG';

export interface CurrencyConfig {
  suffix?: string;
  prefix?: string;
  decimals?: number;
  preferLocale?: string; // overwrites locale for number formatting
}

export const CUSTOM_CURRENCY_FORMATS: { [locale in SupportedLocale]: CurrencyConfig } = {
  'cs-CZ': { suffix: ' Kč' },
  'da-DK': { suffix: ' kr' },
  'de-AT': { prefix: '€ ' },
  'de-CH': { prefix: 'CHF ', suffix: '.-' },
  'de-DE': { suffix: ' €', decimals: 2 },
  'de-HU': { suffix: ' Ft' },
  'de-LU': { suffix: ' €' },
  'el-CY': { prefix: '€', decimals: 2 },
  'el-GR': { suffix: ' €', decimals: 2 },
  'en-AE': { suffix: ' USD', decimals: 2 },
  'en-AU': { decimals: 2 },
  'en-CH': { prefix: 'CHF ', suffix: '.-' },
  'en-CN': { suffix: ' RMB', decimals: 2 },
  'en-DE': { suffix: ' €', decimals: 2 },
  'en-EG': { prefix: 'EGP ' },
  'en-GB': { prefix: '£' },
  'en-HK': { prefix: 'HK$' },
  'en-HU': { suffix: ' Ft' },
  'en-IE': { suffix: ' €' },
  'en-IN': { prefix: '₹ ', decimals: 2 },
  'en-KR': { suffix: ' KRW', decimals: 2 },
  'en-MX': { prefix: '$', suffix: ' MXN' },
  'en-MY': { suffix: ' MYR', decimals: 2 },
  'en-NZ': { suffix: ' NZD', decimals: 2 },
  'en-SE': { suffix: ' kr', decimals: 2 },
  'en-SG': { suffix: ' SGD', decimals: 2 },
  'en-TH': { suffix: ' Baht', decimals: 2 },
  'en-TW': { suffix: ' 台幣' },
  'en-VN': { suffix: ' ₫' },
  'en-ZA': { prefix: 'R ' },
  'es-AR': { suffix: ' ARS' },
  'es-ES': { suffix: ' €', decimals: 2 },
  'es-MX': { prefix: '$' },
  'fi-FI': { suffix: ' €', decimals: 2 },
  'fr-BE': { suffix: ' €' },
  'fr-CH': { prefix: 'CHF ', suffix: '.-' },
  'fr-FR': { suffix: ' €' },
  'fr-LU': { suffix: ' €' },
  'hi-IN': { prefix: '₹ ' },
  'hu-HU': { suffix: ' Ft' },
  'it-CH': { prefix: 'CHF ', suffix: '.-' },
  'it-IT': { suffix: ' €' },
  'ja-JP': { prefix: '￥', suffix: '～' },
  'ko-KR': { suffix: ' KRW' },
  'nl-BE': { suffix: ' €' },
  'nl-NL': { prefix: '€ ' },
  'no-NO': { prefix: 'kr ', suffix: ',-' },
  'pl-PL': { suffix: ' zł' },
  'pt-BR': { prefix: 'R$ ', decimals: 2 },
  'pt-PT': { suffix: ' €' },
  'ro-RO': { suffix: ' €', decimals: 2 },
  'ru-RU': { suffix: ' руб.', decimals: 2 },
  'sk-SK': { suffix: ' €', decimals: 2 },
  'sl-SI': { suffix: ' €', decimals: 2 },
  'sr-RS': { suffix: ' EUR', decimals: 2 },
  'sv-SE': { suffix: ' kr' },
  'th-TH': { suffix: ' บาท', decimals: 2 },
  'tr-TR': { suffix: ' TL' },
  'tr-ZY': { suffix: ' TL' },
  'vi-VN': { suffix: ' ₫' },
  'zh-CN': { decimals: 2 },
  'zh-HK': { prefix: 'HK$' },
  'zh-TW': { prefix: '$' },
  'hr-BA': { suffix: ' KM', decimals: 2 },
  'uk-UA': { suffix: ' ₴', decimals: 2 },
  'ar-EG': { prefix: 'EGP ', preferLocale: 'en-US' },
};
